import { useMemo } from "react";

import { useRoleContext } from "./useRoleContext";
import { useSwrFetch } from "./useSwrFetch";

import type { CompanyLogo } from "types/company";
import type { QuoteCarrierLogosResponse } from "types/quote/carrierLogos";

interface UseQuoteCarrierLogosProps {
  shipmentId: string | number | undefined;
}

interface UseQuoteCarrierLogosResult {
  logoByCompanyId: Map<number, CompanyLogo>;
  loading: boolean;
  error: string | null;
}

/** The logos of the carriers who have quoted on a shipment, keyed by company id. */
export function useQuoteCarrierLogos(
  props: UseQuoteCarrierLogosProps,
): UseQuoteCarrierLogosResult {
  const { shipmentId } = props;
  const { role } = useRoleContext();

  const url = useMemo(() => {
    if (role?.type !== "SHIPPER" || !shipmentId) {
      return null;
    }
    return `/shippers/${role.id}/shipments/${shipmentId}/quote-carrier-logos`;
  }, [role, shipmentId]);

  const { data, loading, error } = useSwrFetch<QuoteCarrierLogosResponse>({
    url,
    errorMessage: "Failed to fetch carrier logos",
  });

  const logoByCompanyId = useMemo(
    () =>
      new Map(
        (data?.carrier_logos ?? []).map((carrierLogo) => [
          carrierLogo.company_id,
          carrierLogo.logo,
        ]),
      ),
    [data],
  );

  return { logoByCompanyId, loading, error };
}
